import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);

  useEffect(() => {
    // Only surface the bootstrap link when there are no users yet
    // (initial Super Admin creation). Otherwise this app is internal-only:
    // new users must be created by an existing Super Admin in Settings.
    api.get("/auth/bootstrap-status").then(r => setNeedsBootstrap(!!r.data?.needs_bootstrap)).catch(() => setNeedsBootstrap(false));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Signed in");
      // MP10 — Industry Beta Reviewers never see the internal dashboard.
      // Route them directly to the restricted Beta Catalog surface.
      if (u && u.role === "industry_beta_reviewer") {
        navigate("/beta");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(errMsg(err, "Sign in failed"));
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="brand-strip text-white p-10 md:p-14 hidden md:flex flex-col justify-between relative overflow-hidden">
        {/* Decorative ambient sunburst (low opacity), reinforces brand without distorting the logo */}
        <div
          className="pointer-events-none absolute -right-32 -bottom-32 opacity-[0.10]"
          aria-hidden="true"
        >
          <BrandLogo variant="kandake-gold" size="2xl" />
        </div>

        {/* Top: GFMP operating-authority chip (small) */}
        <div className="relative flex items-center gap-3" data-testid="login-gfmp-chip">
          <BrandLogo variant="gfmp" size="sm" withRing testId="login-gfmp-logo" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-75">Operating authority</div>
            <div className="text-sm font-medium leading-tight">Good Friar Music Publishing</div>
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-65">The Durban 49 Group</div>
          </div>
        </div>

        {/* Center: Kandake leads the panel */}
        <div className="relative flex flex-col items-start" data-testid="login-kandake-lockup">
          <BrandLogo variant="kandake-gold" size="2xl" testId="login-kandake-gold-logo" frame="square" className="-ml-2 mb-2" />
          <div className="font-display text-[44px] leading-[1.05] tracking-tight" style={{ color: "hsl(40 48% 80%)" }}>
            Kandake Prime
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] opacity-80 mt-1">
            Internal Alpha · GFMP Command Center
          </div>
          <p className="font-display text-xl leading-snug max-w-md mt-6 opacity-95">
            A private operating room for catalog readiness, compliance, and licensing workflow.
          </p>
        </div>

        {/* Bottom: tagline + Powered-by */}
        <div className="relative">
          <p className="text-sm opacity-80 max-w-md">
            <span className="block font-medium text-white/95">GFMP / Durban 49 internal access only.</span>
            <span className="block mt-1">Compliance support platform. Not legal advice.</span>
          </p>
          <div className="inline-flex items-center gap-2 mt-6 px-2.5 py-1 rounded-md" data-testid="login-powered-by"
            style={{ background: "rgba(0,0,0,0.25)", border: "1px solid hsl(40 48% 45% / 0.35)" }}>
            <BrandLogo variant="kandake-gold" size="xs" ariaHidden />
            <span className="text-[10px] uppercase tracking-[0.22em] opacity-90">Powered by Kandake</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 bg-paper">
        <Card className="w-full max-w-md shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-medium" style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)", border: "1px solid hsl(40 48% 78%)" }} data-testid="login-internal-alpha-badge">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "hsl(40 48% 45%)" }} /> Internal Alpha
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Lock className="w-3 h-3" /> Internal use only
              </span>
            </div>
            <h1 className="font-display text-3xl text-foreground">Sign in</h1>
            <p className="text-sm text-muted-foreground mt-1">Kandake Prime Internal Alpha · GFMP / Durban 49 internal access only.</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required autoComplete="email" data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required autoComplete="current-password" data-testid="login-password-input" />
                <div className="mt-1.5 text-right">
                  <Link
                    to="/forgot-password"
                    className="text-[12px] text-muted-foreground hover:text-foreground underline"
                    data-testid="login-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button" style={{background:"hsl(347 72% 22%)"}}>{busy ? "Signing in…" : "Sign in"}</Button>
            </form>
            {needsBootstrap && (
              <div className="text-sm text-muted-foreground mt-6 text-center" data-testid="login-bootstrap-link">
                First-time setup detected. <Link to="/signup" className="text-foreground underline">Create initial Super Admin</Link>
              </div>
            )}
            <div className="text-[11px] text-muted-foreground mt-6 leading-relaxed text-center">
              Compliance support platform. Not legal advice. Human review required before external submission.
            </div>
            <div className="text-center mt-4 text-[11px]">
              <Link to="/" className="text-muted-foreground hover:text-foreground underline" data-testid="login-back-to-landing">← Back to Kandake home</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
